// client/src/components/ImportWizard.jsx
// Generic, reusable XLSX import wizard (wave-10). Drive it with a column
// `spec` (for the format guide) + `analyze`/`commit` async functions that
// hit a resource's import endpoint. Used by StudentsImportModal today;
// any resource can reuse it by supplying its own spec + handlers.
//
// Flow: select file → ANALYZE (server dry-run) → review (column report +
// projected summary + per-row errors) → COMMIT → done (final summary).
// The server returns an identical shape for dry-run and commit:
//   {
//     columns: { recognized[], readOnly[], missingRequired[],
//                missingOptional[], ignored[] },
//     summary: { total, created, updated, unchanged, errors },
//     skipped: [{ row, errors[] }],
//     blocked: bool,   // a required column is missing → cannot import
//   }

import { useState } from 'react';
import Modal from './Modal.jsx';
import { ExclamationTriangleIcon } from './Icons.jsx';

export default function ImportWizard({
  open,
  onClose,
  title,
  entityName = 'רשומות',
  spec = [],
  analyze,
  commit,
}) {
  const [step, setStep] = useState('select'); // 'select' | 'review' | 'done'
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);

  function reset() {
    setStep('select');
    setFile(null);
    setBusy(false);
    setError(null);
    setReport(null);
    setResult(null);
  }
  function close() {
    reset();
    onClose();
  }

  async function run(fn, onOk) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      onOk(await fn(file));
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={close} title={title} maxWidth="max-w-2xl">
      {step === 'select' && (
        <SelectStep
          spec={spec}
          file={file}
          setFile={setFile}
          busy={busy}
          error={error}
          onCancel={close}
          onNext={() => run(analyze, (r) => { setReport(r); setStep('review'); })}
        />
      )}

      {step === 'review' && report && (
        <ReviewStep
          report={report}
          entityName={entityName}
          busy={busy}
          error={error}
          onBack={() => { setError(null); setStep('select'); }}
          onConfirm={() => run(commit, (r) => { setResult(r); setStep('done'); })}
        />
      )}

      {step === 'done' && result && (
        <DoneStep result={result} entityName={entityName} onClose={close} />
      )}
    </Modal>
  );
}

/* ───────── Step 1: select file ───────── */

function SelectStep({ spec, file, setFile, busy, error, onCancel, onNext }) {
  return (
    <div className="space-y-5">
      <div className="card-soft p-4">
        <h3 className="text-sm font-bold text-brand-900 mb-2">מבנה הקובץ</h3>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-ink-700">
          {spec.map((c) => (
            <li key={c.label} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
              <span>{c.label}</span>
              {c.required ? (
                <span className="badge-joy text-[10px]">חובה</span>
              ) : (
                <span className="text-xs text-ink-400">(לא חובה)</span>
              )}
              {c.note && <span className="text-xs text-ink-400">· {c.note}</span>}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-ink-500">
          שורה ראשונה = כותרות. העמודות מזוהות לפי שם הכותרת — הסדר לא משנה.
          לאחר העלאה תוצג בדיקה מלאה לפני הביצוע. עד 2000 שורות, קובץ עד 2MB.
        </p>
      </div>

      <DropZone file={file} setFile={setFile} />

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          ביטול
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onNext}
          disabled={!file || busy}
        >
          {busy ? 'בודק את הקובץ...' : 'בדיקה לפני ייבוא'}
        </button>
      </div>
    </div>
  );
}

/* ───────── Step 2: review (dry-run) ───────── */

function ReviewStep({ report, entityName, busy, error, onBack, onConfirm }) {
  const { summary, blocked } = report;
  const actionable = summary.created + summary.updated;
  const canCommit = !blocked && actionable > 0;
  const dups = report.columns?.duplicates || [];

  return (
    <div className="space-y-4">
      <ColumnReport columns={report.columns} />

      {blocked ? (
        <ErrorBanner>
          {dups.length > 0
            ? `נמצאו עמודות עם אותה כותרת (${dups.map((d) => d.header).join(' · ')}). לכל עמודה חייבת להיות כותרת ייחודית — תקנו את הקובץ ונסו שוב. לא בוצע ייבוא.`
            : 'חסרות עמודות חובה — יש להוסיף אותן לקובץ ולנסות שוב. לא בוצע ייבוא.'}
        </ErrorBanner>
      ) : (
        <>
          <SummaryCards summary={summary} mode="preview" />
          {actionable === 0 && (
            <div className="rounded-2xl bg-ink-50 border border-ink-200 text-ink-600 px-4 py-3 text-sm">
              אין {entityName} חדשים או מעודכנים לביצוע — כל השורות התקינות כבר עדכניות.
            </div>
          )}
        </>
      )}

      <SkippedTable skipped={report.skipped} />

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onBack} disabled={busy}>
          חזרה
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onConfirm}
          disabled={!canCommit || busy}
        >
          {busy
            ? 'מייבא...'
            : `אשר וייבא (${actionable})`}
        </button>
      </div>
    </div>
  );
}

/* ───────── Step 3: done (committed) ───────── */

function DoneStep({ result, entityName, onClose }) {
  const { summary } = result;
  const parts = [];
  if (summary.created > 0) parts.push(`נוספו ${summary.created}`);
  if (summary.updated > 0) parts.push(`עודכנו ${summary.updated}`);
  if (summary.unchanged > 0) parts.push(`ללא שינוי ${summary.unchanged}`);
  if (parts.length === 0) parts.push('לא בוצעו שינויים');

  return (
    <div className="space-y-4">
      <div className="card p-5 bg-mint-50 border-mint-200 text-mint-900 flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-mint-500 text-white grid place-items-center shrink-0">
          <CheckIcon className="w-6 h-6" />
        </div>
        <div>
          <strong className="block text-lg">הייבוא הושלם</strong>
          <span className="text-sm num">{parts.join(' · ')}</span>
        </div>
      </div>

      <SummaryCards summary={summary} mode="done" />
      <SkippedTable skipped={result.skipped} />

      <div className="flex justify-end pt-1">
        <button type="button" className="btn-primary" onClick={onClose}>
          סגור
        </button>
      </div>
    </div>
  );
}

/* ───────── Column report ───────── */

function ColumnReport({ columns }) {
  const { recognized = [], readOnly = [], missingRequired = [], missingOptional = [], ignored = [], duplicates = [] } = columns;
  return (
    <div className="space-y-2.5">
      {duplicates.length > 0 && (
        <ReportLine tone="danger" Icon={ExclamationTriangleIcon} title="עמודות עם כותרת כפולה">
          {duplicates.map((d) => `${d.header} (×${d.count})`).join(' · ')}
        </ReportLine>
      )}
      {missingRequired.length > 0 && (
        <ReportLine tone="danger" Icon={ExclamationTriangleIcon} title="עמודות חובה חסרות">
          {missingRequired.map((c) => c.label).join(' · ')}
        </ReportLine>
      )}
      {recognized.length > 0 && (
        <ReportLine tone="ok" Icon={CheckIcon} title={`עמודות מזוהות (${recognized.length})`}>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {recognized.map((c) => (
              <span key={c.key} className="badge bg-mint-100 text-mint-800 border border-mint-200">
                {c.label}
              </span>
            ))}
          </div>
        </ReportLine>
      )}
      {ignored.length > 0 && (
        <ReportLine tone="muted" Icon={InfoIcon} title={`עמודות בקובץ שלא יטופלו (${ignored.length})`}>
          {ignored.map((c) => c.header).join(' · ')}
        </ReportLine>
      )}
      {missingOptional.length > 0 && (
        <ReportLine tone="muted" Icon={InfoIcon} title="עמודות אופציונליות שלא נמצאו">
          {missingOptional.map((c) => c.label).join(' · ')}
        </ReportLine>
      )}
      {readOnly.length > 0 && (
        <ReportLine tone="muted" Icon={InfoIcon} title="עמודות לקריאה בלבד (לא יישמרו)">
          {readOnly.map((c) => c.label).join(' · ')}
        </ReportLine>
      )}
    </div>
  );
}

const REPORT_TONE = {
  ok: 'bg-mint-50 border-mint-200 text-mint-900',
  danger: 'bg-joy-50 border-joy-200 text-joy-900',
  muted: 'bg-ink-50 border-ink-200 text-ink-600',
};

function ReportLine({ tone, Icon, title, children }) {
  return (
    <div className={`rounded-2xl border px-4 py-2.5 text-sm ${REPORT_TONE[tone]}`}>
      <div className="flex items-center gap-2 font-bold">
        <Icon className="w-4 h-4 shrink-0" />
        <span>{title}</span>
      </div>
      <div className="mt-0.5 ms-6">{children}</div>
    </div>
  );
}

/* ───────── Summary cards ───────── */

function SummaryCards({ summary, mode }) {
  const preview = mode === 'preview';
  const cards = [
    { label: 'סה״כ שורות', value: summary.total, tone: 'ink' },
    { label: preview ? 'יתווספו' : 'נוספו', value: summary.created, tone: 'mint' },
    { label: preview ? 'יעודכנו' : 'עודכנו', value: summary.updated, tone: 'sky' },
    { label: 'ללא שינוי', value: summary.unchanged, tone: 'ink' },
    { label: 'שגיאות', value: summary.errors, tone: summary.errors > 0 ? 'joy' : 'ink' },
  ];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
      {cards.map((c) => (
        <div key={c.label} className={`rounded-2xl border p-3 text-center ${STAT_TONE[c.tone]}`}>
          <div className="text-2xl font-extrabold num">{c.value}</div>
          <div className="text-[11px] font-bold mt-0.5">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

const STAT_TONE = {
  ink:  'bg-white border-ink-200 text-ink-800',
  mint: 'bg-mint-50 border-mint-200 text-mint-800',
  sky:  'bg-sky-50 border-sky-200 text-sky-800',
  joy:  'bg-joy-50 border-joy-200 text-joy-800',
};

/* ───────── Skipped rows ───────── */

function SkippedTable({ skipped }) {
  if (!skipped || skipped.length === 0) return null;
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 bg-joy-50 border-b border-joy-200 text-sm font-bold text-joy-900 num">
        שורות שנדחו ({skipped.length})
      </div>
      <ul className="divide-y divide-ink-100 max-h-64 overflow-y-auto">
        {skipped.map((s) => (
          <li key={s.row} className="px-4 py-2.5 text-sm">
            <span className="font-semibold text-ink-900 num">שורה {s.row}</span>
            <ul className="ms-3 mt-0.5 list-disc list-inside text-xs text-joy-700">
              {s.errors.map((e, idx) => (
                <li key={idx}>{e}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ───────── Bits ───────── */

function DropZone({ file, setFile }) {
  // Drag-and-drop was advertised ("או גרור לכאן") but never wired — a bare
  // <label> only handles click. Add dragover/drop so dropping a file works,
  // and preventDefault so the browser doesn't navigate to the dropped file.
  const [dragging, setDragging] = useState(false);
  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  }
  return (
    <label
      onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
      onDrop={onDrop}
      className={`block rounded-2xl border-2 border-dashed transition-colors cursor-pointer
        ${dragging ? 'border-brand-500 bg-brand-50'
          : file ? 'border-mint-400 bg-mint-50'
          : 'border-ink-300 hover:border-brand-400 hover:bg-brand-50/40'}`}
    >
      <input
        type="file"
        accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="sr-only"
      />
      <div className="p-6 text-center">
        {file ? (
          <>
            <div className="mx-auto w-12 h-12 rounded-2xl bg-mint-100 text-mint-700 grid place-items-center mb-2">
              <FileIcon className="w-6 h-6" />
            </div>
            <div className="text-sm font-semibold text-ink-900">{file.name}</div>
            <div className="text-xs text-ink-500 num mt-0.5">
              {Math.round(file.size / 1024)} KB · לחץ להחלפת קובץ
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto w-12 h-12 rounded-2xl bg-brand-100 text-brand-700 grid place-items-center mb-2">
              <UploadIcon className="w-6 h-6" />
            </div>
            <div className="text-sm font-semibold text-ink-900">בחר קובץ XLSX</div>
            <div className="text-xs text-ink-500 mt-0.5">או גרור לכאן</div>
          </>
        )}
      </div>
    </label>
  );
}

function ErrorBanner({ children }) {
  return (
    <div className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2">
      <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

function humanizeError(e) {
  const code = e?.code || e?.message || '';
  const msg = e?.message && e.message !== e?.code ? e.message : '';
  // The server sends an actionable Hebrew message for an oversized / over-long
  // file — show it verbatim instead of a generic line, so the user sees the
  // real reason (e.g. clean phantom rows and re-export).
  if (e?.status === 413 || /file_too_large/.test(code)) return msg || 'הקובץ גדול מדי. צמצמו אותו ונסו שוב.';
  if (/too_large/.test(code) || /שורות נתונים/.test(msg)) return msg || 'הקובץ גדול מדי (עד 2000 שורות).';
  if (/xlsx_empty/.test(code)) return 'הקובץ ריק או לא תקין.';
  if (e?.status === 403) return 'אין לך הרשאה לפעולה זו.';
  if (e?.status === 401) return 'הסשן פג. רענן את הדף והיכנס מחדש.';
  if (e?.status >= 500) return `שגיאת שרת (${e.status}). נסה שוב בעוד רגע.`;
  return `שגיאה: ${e?.message || e?.code || 'לא צפויה'}`;
}

function UploadIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 7.5m0 0L7.5 12M12 7.5v9" />
    </svg>
  );
}
function FileIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}
function CheckIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}
function InfoIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  );
}
